// oep-retention-strategy.js - Tier 1 Educational
export const content = {
  heroStyle: 'dark',
  tag: 'OEP Playbook',

  html: `
<p>If AEP is when you gain clients, OEP is when you can lose them. The Medicare Advantage Open Enrollment Period—January 1 through March 31—is often overlooked by agents focused on recovering from AEP chaos. But for your competitors, it's a second chance to poach your clients.</p>

<p>Here's what I've learned about the OEP: it's not just a threat. If you play it right, it's actually an opportunity to solidify relationships and catch problems before they become reasons to leave next AEP.</p>

<h2>Understanding the OEP Landscape</h2>

<p>During OEP, Medicare Advantage enrollees can:</p>
<ul>
<li>Switch from one MA plan to another MA plan (one time)</li>
<li>Drop MA and return to Original Medicare + Part D (one time)</li>
</ul>

<p>They cannot join MA from Original Medicare during OEP—that's AEP only. But for your existing MA clients, it's an open window to change.</p>

<p>Why does this matter? Because your clients who enrolled in October/November are now actually using their new coverage. They're hitting the pharmacy, seeing doctors, navigating networks. And some of them are discovering that what sounded good during AEP doesn't work as well in practice.</p>

<h2>The First 90 Days Problem</h2>

<p>Research shows that about 15.6% of new MA enrollees change insurance within their first year—and a significant chunk of that happens during OEP. The first 90 days of a new plan are when friction points emerge:</p>

<ul>
<li>Their doctor isn't actually in-network (or left the network January 1)</li>
<li>Their medications have prior auth requirements they didn't know about</li>
<li>The pharmacy they prefer isn't convenient in the new network</li>
<li>The premium hit their bank account and felt more real than it did in October</li>
<li>They got a claim denied or had a confusing EOB</li>
</ul>

<p>If you enrolled them and disappeared, these friction points become reasons to switch. If you're present and helping, they become service opportunities that build loyalty.</p>

<h2>The OEP Retention Strategy</h2>

<h3>The January Check-In Call</h3>

<p>Within the first two weeks of January, call every client you enrolled in the previous AEP:</p>

<p><em>"Hey, I wanted to check in now that your new coverage is active. Have you had a chance to use it yet? Any questions or issues I can help with?"</em></p>

<p>This call accomplishes several things:</p>
<ul>
<li>Shows you care beyond the sale</li>
<li>Catches problems while they're small</li>
<li>Prevents the "I'll just switch during OEP" reaction</li>
<li>Creates a touchpoint that competitors won't match</li>
</ul>

<p>Most agents don't make this call. They're burned out from AEP, taking vacation, or already focused on the next thing. That's exactly why it's so effective—it sets you apart.</p>

<h3>Proactive Problem-Solving</h3>

<p>When you identify an issue during that check-in, don't wait for the client to figure it out:</p>

<ul>
<li><strong>Doctor not in network?</strong> Help them understand their options—find a new PCP, look into out-of-network costs, or discuss whether an OEP switch makes sense</li>
<li><strong>Medication issue?</strong> Walk them through the prior auth process, help them contact the plan, explore alternative medications if needed</li>
<li><strong>Confused about how to use coverage?</strong> Take 10 minutes to explain the basics they missed during enrollment</li>
</ul>

<p>The key is making their problem your problem. They shouldn't have to navigate the healthcare system alone.</p>

<h3>The "Is This Still Working?" Conversation</h3>

<p>For some clients, the plan really isn't right. Maybe you had incomplete information during enrollment, or circumstances changed, or the plan itself changed in ways that weren't anticipated.</p>

<p>Don't be afraid to have this conversation:</p>

<p><em>"Based on what you're telling me, this plan isn't working as well as we hoped. Let's look at your options. During OEP, you can switch to a different MA plan, or you could go back to Original Medicare with a supplement. What matters most to you?"</em></p>

<p>Yes, this might mean moving them to a different plan—but keeping the client in a plan that works is better than losing them entirely to a competitor or to frustration.</p>

<h3>Defending Against OEP Poaching</h3>

<p>Your clients are being marketed to during OEP. Mailers, TV ads, carrier outreach—it's AEP lite. Your job is to make sure they feel served before they start listening to alternatives.</p>

<p>Tactics:</p>
<ul>
<li><strong>Be first:</strong> Your January call should happen before they get frustrated enough to take another agent's call</li>
<li><strong>Be helpful:</strong> Every problem you solve is a reason not to switch</li>
<li><strong>Be honest:</strong> If their plan isn't working, tell them—and help them fix it with you, not someone else</li>
<li><strong>Be present:</strong> A client who hears from you in January is less likely to answer a cold call from a competitor</li>
</ul>

<h2>OEP for Established Clients Too</h2>

<p>OEP isn't just about new enrollees. Your established MA clients can also switch during this window. While they're less likely to do so (they've already made it through previous years), they're not immune.</p>

<p>Reasons established clients might use OEP:</p>
<ul>
<li>Plan made significant changes for the new year that now feel problematic</li>
<li>Premium increase feels harder to swallow than expected</li>
<li>Network change affected their care</li>
<li>They got a compelling offer from a competitor</li>
</ul>

<p>A February touchpoint for your established book can catch these situations:</p>

<p><em>"Just checking in a few weeks into the new year. How's everything going with your coverage? Any changes from last year that are affecting you?"</em></p>

<h2>Tracking OEP Activity</h2>

<p>You should know:</p>
<ul>
<li>Which clients you enrolled last AEP (highest OEP risk)</li>
<li>Which clients have contacted you with issues since January 1</li>
<li>Which clients you haven't been able to reach</li>
<li>Which plans in your book have had the most friction reports</li>
</ul>

<p>If you're seeing patterns—"a lot of clients on Plan X are having pharmacy issues"—that's intelligence you can use to proactively reach out to other Plan X clients.</p>

<h2>The OEP Opportunity</h2>

<p>OEP is also an opportunity, not just a threat. If you have good relationships with clients on plans that aren't serving them well—even if you didn't originally enroll them—OEP is a time to help:</p>

<p><em>"I know you enrolled with another agent, but I'm here to help. If your current plan isn't working, let's look at options together."</em></p>

<p>This is how you grow your book during OEP—by being the agent who helps when others won't.</p>

<h2>After OEP: The Foundation for Next AEP</h2>

<p>Everything you do during OEP sets up your AEP retention. A client who felt served during January-March is a client who's not looking to switch come October. The problems you solved become stories of why they trust you.</p>

<p>Think of OEP as the first quarter of your retention year. Get it right, and the rest of the year is much easier.</p>
  `,

  faqs: [
    {
      question: "What is OEP and who can use it?",
      answer: "The Medicare Advantage Open Enrollment Period runs January 1-March 31. During OEP, MA enrollees can switch to a different MA plan or drop MA and return to Original Medicare (with Part D). They can only make one change during OEP. People on Original Medicare cannot use OEP to join MA."
    },
    {
      question: "When should I contact clients during OEP?",
      answer: "Contact new enrollees (from the previous AEP) within the first two weeks of January—before they encounter problems and start shopping. For established clients, a February touchpoint works well to catch any issues from plan year changes."
    },
    {
      question: "What if I discover a client's plan isn't working well?",
      answer: "Be honest with them. During OEP, they can switch to a different MA plan or return to Original Medicare. It's better to help them make a change that works than to lose them to a competitor. Keeping the relationship matters more than keeping them on a specific plan."
    },
    {
      question: "How do I protect my clients from OEP poaching?",
      answer: "Be first and be helpful. Contact clients before they get frustrated enough to take a competitor's call. Solve problems proactively. A client who feels served is much less likely to listen to alternative offers."
    },
    {
      question: "Should I reach out to clients I didn't enroll?",
      answer: "Yes, if you have a relationship with them. OEP is an opportunity to help clients who are unhappy with their current coverage, even if you weren't their original agent. Being helpful when others aren't is how you grow during OEP."
    }
  ],

  sources: [
    {
      title: "Medicare Advantage Open Enrollment Period",
      url: "https://www.medicare.gov/sign-up-change-plans/joining-a-health-or-drug-plan",
      publisher: "Medicare.gov",
      accessedDate: "2024-12-10"
    },
    {
      title: "Turnover Among New Medicare Advantage Enrollees",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9586458/",
      publisher: "AJMC/PMC",
      accessedDate: "2024-12-10"
    }
  ],

  ctaDescription: `OEP is when new enrollees either become loyal clients or start shopping. Pro IRP automatically flags clients who need January check-ins, tracks service issues, and helps you stay ahead of OEP churn before it happens.`
};

export default content;
